import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Zap } from 'lucide-react';
import { getLanguageName } from '../services/languageConstants';
import T from './T';

export interface ExerciseCategorySelection {
  mode: 'existing' | 'new';
  deckName: string;
}

export interface GenerateExerciseModalConfig {
  flashcards: ExerciseCategorySelection;
  pronunciation: ExerciseCategorySelection;
  writing: ExerciseCategorySelection;
}

interface GenerateExerciseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: GenerateExerciseModalConfig) => void;
  itemCount: number;
  bookLangCode: string;
  existingFlashcardDecks: string[];
  existingPronunciationDecks: string[];
  existingWritingDecks: string[];
}

const CategorySelector: React.FC<{
  title: string;
  existingDecks: string[];
  defaultLangName: string;
  value: ExerciseCategorySelection;
  onChange: (val: ExerciseCategorySelection) => void;
}> = ({ title, existingDecks, defaultLangName, value, onChange }) => {
  return (
    <div className="bg-tint-stone/40 p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
      <h4 className="text-sm font-medium text-ink mb-3 flex items-center gap-2">
        {title}
      </h4>
      <div className="flex gap-2 p-1 bg-tint-stone rounded-xl mb-3">
        <button
          onClick={() => onChange({ mode: 'existing', deckName: existingDecks[0] || '' })}
          className={`flex-1 py-2 text-[10px] font-normal uppercase font-mono tracking-[0.14em] rounded-lg transition-all ${value.mode === 'existing' ? 'bg-surface text-primary' : 'text-muted'}`}
        >
          Existente
        </button>
        <button
          onClick={() => onChange({ mode: 'new', deckName: defaultLangName })}
          className={`flex-1 py-2 text-[10px] font-normal uppercase font-mono tracking-[0.14em] rounded-lg transition-all ${value.mode === 'new' ? 'bg-surface text-primary' : 'text-muted'}`}
        >
          Novo
        </button>
      </div>

      {value.mode === 'existing' ? (
        existingDecks.length > 0 ? (
          <select
            value={value.deckName}
            onChange={(e) => onChange({ ...value, deckName: e.target.value })}
            className="w-full bg-surface text-ink border border-black/[0.06] rounded-xl px-4 py-3 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all font-medium appearance-none text-sm"
          >
            {existingDecks.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        ) : (
          <p className="text-xs text-warn font-medium"><T k="ui.genEx.noDeck" /></p>
        )
      ) : (
        <input
          type="text"
          value={value.deckName}
          onChange={(e) => onChange({ ...value, deckName: e.target.value })}
          placeholder="Nome do novo baralho..."
          className="w-full bg-surface text-ink border border-black/[0.06] rounded-xl px-4 py-3 outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all font-medium text-sm placeholder:text-muted"
        />
      )}
    </div>
  );
};

export const GenerateExerciseModal: React.FC<GenerateExerciseModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemCount,
  bookLangCode,
  existingFlashcardDecks,
  existingPronunciationDecks,
  existingWritingDecks
}) => {
  const { t } = useTranslation();
  const defaultLangName = getLanguageName(bookLangCode);

  const [flashcards, setFlashcards] = useState<ExerciseCategorySelection>({ mode: 'new', deckName: defaultLangName });
  const [pronunciation, setPronunciation] = useState<ExerciseCategorySelection>({ mode: 'new', deckName: defaultLangName });
  const [writing, setWriting] = useState<ExerciseCategorySelection>({ mode: 'new', deckName: defaultLangName });

  // Initialize modes based on whether decks exist
  useEffect(() => {
    if (isOpen) {
      setFlashcards(existingFlashcardDecks.length > 0 
        ? { mode: 'existing', deckName: existingFlashcardDecks[0] } 
        : { mode: 'new', deckName: defaultLangName });
        
      setPronunciation(existingPronunciationDecks.length > 0 
        ? { mode: 'existing', deckName: existingPronunciationDecks[0] } 
        : { mode: 'new', deckName: defaultLangName });
        
      setWriting(existingWritingDecks.length > 0 
        ? { mode: 'existing', deckName: existingWritingDecks[0] } 
        : { mode: 'new', deckName: defaultLangName });
    }
  }, [isOpen, existingFlashcardDecks, existingPronunciationDecks, existingWritingDecks, defaultLangName]);

  if (!isOpen) return null;

  const isInvalid = (selection: ExerciseCategorySelection) => {
    return selection.mode === 'new' ? !selection.deckName.trim() : !selection.deckName;
  };

  const handleConfirm = () => {
    if (isInvalid(flashcards) || isInvalid(pronunciation) || isInvalid(writing)) return;
    onConfirm({ flashcards, pronunciation, writing });
  };

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center p-4 sm:p-6 bg-black/30 backdrop-blur-md animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-surface p-6 sm:p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200 flex flex-col gap-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-warn/10 text-warn rounded-2xl flex items-center justify-center">
              <Zap size={24} fill="currentColor" />
            </div>
            <div>
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em]"><T k="ui.genEx.title" /></h3>
              <p className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary mt-1">{itemCount} anotações • {defaultLangName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-muted hover:bg-tint-stone/60 rounded-full transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-muted font-medium"><T k="ui.genEx.where" /></p>
          
          <CategorySelector
            title="🎯 Flashcards"
            existingDecks={existingFlashcardDecks}
            defaultLangName={defaultLangName}
            value={flashcards}
            onChange={setFlashcards}
          />
          
          <CategorySelector
            title="🗣️ Pronúncia"
            existingDecks={existingPronunciationDecks}
            defaultLangName={defaultLangName}
            value={pronunciation}
            onChange={setPronunciation}
          />
          
          <CategorySelector
            title="✍️ Escrita"
            existingDecks={existingWritingDecks}
            defaultLangName={defaultLangName}
            value={writing}
            onChange={setWriting}
          />
        </div>

        <button
          onClick={handleConfirm}
          disabled={isInvalid(flashcards) || isInvalid(pronunciation) || isInvalid(writing)}
          className="text-[13px] font-medium mt-2 w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 disabled:pointer-events-none"
        >
          Confirmar Geração
        </button>
      </div>
    </div>
  );
};
